import React from 'react';
import {
  Modal,
  ModalSection,
  Gutter,
  Banner,
  Layout,
  Icon,
  Flashy,
  CondensedLarge,
  ParagraphHeader,
} from '@axiom/ui';
import type { AfcSubmission, CompatibilityResults } from '@axiom/validation';

import type { InterviewCandidate } from '../../types/interview-candidate.types';
import { TalentInterviewModalHeader } from '../TalentInterviewModalHeader/TalentInterviewModalHeader';
import { TalentInterviewModalFooter } from '../TalentInterviewModalFooter/TalentInterviewModalFooter';

export interface TalentInterviewWarningModalProps {
  candidate: InterviewCandidate;
  compatibilityResults: CompatibilityResults;
  submission?: AfcSubmission;
  onClose: () => void;
  onContinue: () => void | Promise<void>;
}

export const getWarningMessages = (
  results: CompatibilityResults,
  candidate: InterviewCandidate,
  worksite: string,
  opportunityAddressState?: string
): Array<{
  id: string;
  message: string;
}> => {
  const warnings = [];

  if (!results.hasMatchingBarredLocation) {
    if (worksite === 'Remote') {
      warnings.push({
        id: 'barred-location-remote',
        message: `Not admitted in ${candidate.addressState}`,
      });
    } else if (worksite === 'Onsite/Remote') {
      warnings.push({
        id: 'barred-location-onsite-remote',
        message: 'Not admitted in required State',
      });
    } else {
      warnings.push({
        id: 'barred-location-onsite',
        message: `Not admitted in ${opportunityAddressState || 'required State'}`,
      });
    }
  }

  if (!results.isAvailable) {
    warnings.push({
      id: 'availability',
      message: `Only available for ${candidate.weeklyAvailability} hours per week`,
    });
  }

  if (!results.meetsMinPayRequirements) {
    warnings.push({
      id: 'min-pay-requirements',
      message: `Cannot be engaged for less than ${candidate.minWeeklyHoursPerPosition} hours per week`,
    });
  }

  if (!results.jobProfileMeetsMinHoursRequirements) {
    warnings.push({
      id: 'min-hours-requirements',
      message: 'Cannot be engaged for less than 40 hours per week',
    });
  }

  return warnings;
};

export const TalentInterviewWarningModal = ({
  candidate,
  compatibilityResults,
  submission,
  onClose,
  onContinue,
}: TalentInterviewWarningModalProps) => {
  const submissionPosition = submission?.positions[0];
  const worksite = submissionPosition?.worksite ?? 'Remote';
  const opportunityAddressState = submission?.addressState ?? undefined;

  const warnings = getWarningMessages(
    compatibilityResults,
    candidate,
    worksite,
    opportunityAddressState
  );

  return (
    <Modal name="INTERVIEW_COMPATIBILITY_WARNING_MODAL">
      <TalentInterviewModalHeader
        candidate={candidate}
        name="INTERVIEW_COMPATIBILITY_WARNING_MODAL_HEADER"
        onClose={onClose}
      />
      <ModalSection>
        <ParagraphHeader name="SECTION_DESCRIPTION">
          It looks like{' '}
          {candidate.calculatedFirstName || candidate.calculatedDisplayName} may
          not match the following requirements. Do you still want to continue
          with this interview request?
        </ParagraphHeader>
        <Gutter bottom="24px" />
        <Banner type="critical">
          <Layout direction="vertical" verticalGutter="8px">
            {warnings.map(warning => (
              <Layout key={warning.id} position="middle" horizontalGutter="8px">
                <Flashy color="dataVizYellow">
                  <Icon name="alert-filled" />
                </Flashy>
                <CondensedLarge>{warning.message}</CondensedLarge>
              </Layout>
            ))}
          </Layout>
        </Banner>
      </ModalSection>
      <TalentInterviewModalFooter
        name="INTERVIEW_COMPATIBILITY_WARNING_MODAL_FOOTER"
        onClose={onClose}
        onContinue={onContinue}
      />
    </Modal>
  );
};
